import { PlayerProperty, Save, Restore } from '../PlayerProperty';
import IPlayerData from '../../IPlayerData';
import { IPlayerState } from '../../../State/Player/IPlayerState';
import {
    dateToString,
    restoreBoolean,
    restoreDate,
    restoreNumber,
    restoreString,
    restoreStringifiedArray,
} from '../../ParseHelper';
import { report } from '../../../report';
import { IClaimedDailyLoginReward } from '../../../Constants/DailyLogin';
import { ProductId } from 'Utility/Product';
import { ArrayHelper } from 'Utility/ArrayHelper';

type PlayerStateKey = 'progress';
type PlayerDataKeys =
    | 'currencyShareCount'
    | 'wheelSpinCount'
    | 'loginStreak'
    | 'optInReward'
    | 'levelsSinceAd'
    | 'shopGiftsCollected'
    | 'adsWatched'
    | 'v1_45_0_totalAdsWatched'
    | 'difficultyReduction'
    | 'v1_44_0_starChestInitialStars'
    | 'v1_62_0_dailyLoginRewardsClaimed'
    | 'v2_7_1_payerReactivationPurchases';

export default class Progress implements PlayerProperty<PlayerStateKey, PlayerDataKeys> {
    public isChanged(playerStateData: IPlayerState, playerEndpointData: IPlayerData): boolean {
        const restoreData = this.restore(playerEndpointData);
        return (
            restoreData.currencyShareCount !== playerStateData.progress.currencyShareCount ||
            restoreData.wheelSpinCount !== playerStateData.progress.wheelSpinCount ||
            restoreData.loginStreak !== playerStateData.progress.loginStreak ||
            !ArrayHelper.compare(
                restoreData.dailyLoginRewardsClaimed,
                playerStateData.progress.dailyLoginRewardsClaimed,
                (elm1: IClaimedDailyLoginReward, elm2: IClaimedDailyLoginReward) => {
                    return elm1.id === elm2.id && elm1.date?.getTime() === elm2.date?.getTime();
                }
            ) ||
            restoreData.optInReward !== playerStateData.progress.optInReward ||
            restoreData.levelsSinceAd !== playerStateData.progress.levelsSinceAd ||
            restoreData.shopGiftsCollected !== playerStateData.progress.shopGiftsCollected ||
            restoreData.adsWatched !== playerStateData.progress.adsWatched ||
            restoreData.totalAdsWatched !== playerStateData.progress.totalAdsWatched ||
            restoreData.difficultyReduction !== playerStateData.progress.difficultyReduction ||
            restoreData.starChestInitialStars !== playerStateData.progress.starChestInitialStars ||
            !ArrayHelper.compare(
                restoreData.payerReactivationPurchases,
                playerStateData.progress.payerReactivationPurchases
            )
        );
    }

    public restore(playerEndpointData: IPlayerData): Restore<PlayerStateKey> {
        const dailyLoginRewardsClaimedValues = restoreStringifiedArray<string | null>(
            playerEndpointData['v1_62_0_dailyLoginRewardsClaimed']
        );
        const dailyLoginRewardsClaimed: IClaimedDailyLoginReward[] = [];
        if (dailyLoginRewardsClaimedValues) {
            for (let i = 0; i < dailyLoginRewardsClaimedValues.length; i += 2) {
                try {
                    const id = restoreString(dailyLoginRewardsClaimedValues[i]);
                    const date = restoreDate(dailyLoginRewardsClaimedValues[i + 1]);
                    if (date === null) {
                        console.warn(`Invalid timestamp: ${dailyLoginRewardsClaimedValues[i + 1]}`);
                        throw new Error('Cannot parse timestamp of daily login reward');
                    }

                    dailyLoginRewardsClaimed.push({
                        id,
                        date,
                    });
                } catch (error) {
                    console.warn('Failed to parse claimed daily login rewards', dailyLoginRewardsClaimedValues);
                    report('Failed to parse claimed daily login rewards', {
                        error,
                        once: true,
                        sampleRate: 0.1,
                    });
                }
            }
        }

        return {
            currencyShareCount: restoreNumber(playerEndpointData.currencyShareCount, 0),
            wheelSpinCount: restoreNumber(playerEndpointData.wheelSpinCount, 0),
            loginStreak: restoreNumber(playerEndpointData.loginStreak, 0),
            optInReward: restoreNumber(playerEndpointData.optInReward, 0),
            levelsSinceAd: restoreNumber(playerEndpointData.levelsSinceAd, 0),
            shopGiftsCollected: restoreNumber(playerEndpointData.shopGiftsCollected, 0),
            adsWatched: restoreNumber(playerEndpointData.adsWatched, 0),
            totalAdsWatched: restoreNumber(playerEndpointData.v1_45_0_totalAdsWatched, 0),
            difficultyReduction: restoreNumber(playerEndpointData.difficultyReduction, 0),
            starChestInitialStars: restoreNumber(playerEndpointData.v1_44_0_starChestInitialStars, 0),
            dailyLoginRewardsClaimed,
            payerReactivationPurchases: restoreReactivationPurchases(),
        };

        function restoreReactivationPurchases(): ProductId[] {
            const payerReactivationPurchases = restoreStringifiedArray<ProductId>(
                playerEndpointData.v2_7_1_payerReactivationPurchases,
                []
            );

            // If purchase was tracked using previous property, add offer to new list
            if (
                restoreBoolean(playerEndpointData.v1_75_0_payerReactivationOfferPurchased, false) &&
                !payerReactivationPurchases.includes(ProductId.PAYER_REACTIVATION_OFFER)
            ) {
                payerReactivationPurchases.push(ProductId.PAYER_REACTIVATION_OFFER);
            }

            return payerReactivationPurchases;
        }
    }

    public save(playerStateData: IPlayerState): Save<PlayerDataKeys> {
        const claimedDailyLoginRewardData: (string | Date)[] = [];
        for (const reward of playerStateData.progress.dailyLoginRewardsClaimed) {
            claimedDailyLoginRewardData.push(reward.id);
            claimedDailyLoginRewardData.push(dateToString(reward.date));
        }

        return {
            currencyShareCount: playerStateData.progress.currencyShareCount,
            wheelSpinCount: playerStateData.progress.wheelSpinCount,
            loginStreak: playerStateData.progress.loginStreak,
            optInReward: playerStateData.progress.optInReward,
            levelsSinceAd: playerStateData.progress.levelsSinceAd,
            shopGiftsCollected: playerStateData.progress.shopGiftsCollected,
            adsWatched: playerStateData.progress.adsWatched,
            v1_45_0_totalAdsWatched: playerStateData.progress.totalAdsWatched,
            difficultyReduction: playerStateData.progress.difficultyReduction,
            v1_44_0_starChestInitialStars: playerStateData.progress.starChestInitialStars,
            v1_62_0_dailyLoginRewardsClaimed: JSON.stringify(claimedDailyLoginRewardData),
            v2_7_1_payerReactivationPurchases: JSON.stringify(
                playerStateData.progress.payerReactivationPurchases ?? []
            ),
        };
    }
}
